import { useCallback, useEffect, useRef, useState } from 'react';
import RollReveal from './RollReveal';
import {
  beginGuardianRoll,
  finishGuardianAttack,
  getGuardianAttack,
  getGuardianRollGame,
  getGuardianRoom,
  settleGuardianRoll,
  startGuardianAttack,
  updateGuardianThrow,
} from './guardianCombat';
import { getWardenShardReward } from './sectorEnemies';
import { getGuardianAttackTiming } from './guardianCamera';
import { flipRoll, resolveBoardTileChoice } from './game';
import { activatePhaseAnchor } from './phaseAnchor';
import { markBountyTile, clearBountyTag } from './bountyTag';
import { recordThrowEvent } from './throwCombo';
import { selectDie } from './dice';
import { activatePortalPair } from './portalPair';
import { playSound } from '../../utils/utils';
import { combatHaptic } from '../../utils/combatHaptics';
import {
  NON_LETHAL_ATTACK_SOUNDS,
  GUARDIAN_ATTACK_SOUNDS,
} from '../../utils/soundEffects';
import RewardCollection from './RewardCollection';
import { getRewardCollectionDuration } from './rewardCollectionTiming';

const GuardianBattle = ({
  game,
  setGame,
  reduceMotion,
  healthRef,
  shardsRef,
  onRewardProgress,
  onVictoryPresented,
  onCombatStage,
}) => {
  const guardian = game.guardian;
  const setup = game.phase === 'guardianReady';
  const striking = game.phase === 'guardianStrike';
  const result = striking ? guardian.lastRound : null;
  const [presentation, setPresentation] = useState(null);
  const cameraHandoffRef = useRef(null);
  const rewardOriginRef = useRef(null);
  const [restoredRound] = useState(() =>
    ['guardianRolled', 'guardianStrike'].includes(game.phase) &&
    Number.isInteger(game.roll)
      ? guardian.round
      : null
  );
  const combatId = result?.combatId;
  const activePresentation =
    combatId && presentation?.id === combatId ? presentation : null;
  const startTime = activePresentation?.startTime || 0;
  const stage = activePresentation?.stage || 'launch';
  useEffect(() => {
    if (combatId) onCombatStage?.({ id: combatId, stage });
  }, [combatId, stage, onCombatStage]);
  const onCombatReady = useCallback(() => {
    if (combatId)
      setPresentation((current) =>
        current?.id === combatId
          ? current
          : {
              id: combatId,
              startTime: Date.now(),
              stage: result?.lastStandResolvedAtImpact ? 'counter' : 'launch',
            }
      );
  }, [combatId, result?.lastStandResolvedAtImpact]);
  const attackTiming = getGuardianAttackTiming(reduceMotion, result);
  const rewardAt = result?.retaliation
    ? attackTiming.counterAt + 120
    : attackTiming.hitAt + (reduceMotion ? 80 : 360);
  const canCollect = !!result && result.playerHpAfter > 0;
  const timing = {
    ...attackTiming,
    finishAt: canCollect
      ? Math.max(
          attackTiming.finishAt,
          rewardAt + getRewardCollectionDuration(result, reduceMotion)
        )
      : attackTiming.finishAt,
  };
  const {
    launchAt,
    hitAt,
    counterWindupAt,
    counterLaunchAt,
    counterAt,
    finishAt,
  } = timing;
  useEffect(() => {
    if (!combatId || !startTime) return undefined;
    if (result.lastStandResolvedAtImpact) {
      const finish = setTimeout(
        () => setGame(finishGuardianAttack),
        reduceMotion ? 200 : 1000
      );
      return () => clearTimeout(finish);
    }
    const updateStage = (next) =>
      setPresentation((current) =>
        current?.id === combatId ? { ...current, stage: next } : current
      );
    const timers = [
      setTimeout(
        () =>
          playSound('/sounds/hero-skill-attack.mp3', {
            preventOverlap: true,
          }),
        launchAt
      ),
      setTimeout(() => {
        updateStage('hit');
        if (result.attackDealt > 0) combatHaptic('dealt');
        if (result.enemyHpAfter === 0) {
          playSound('/sounds/rock-smash.mp3');
          playSound('/sounds/instant-explosion-attack.mp3');
        } else {
          playSound(
            NON_LETHAL_ATTACK_SOUNDS[
              Math.floor(Math.random() * NON_LETHAL_ATTACK_SOUNDS.length)
            ],
            { preventOverlap: true }
          );
        }
      }, hitAt),
      setTimeout(() => {
        if (result.retaliation) {
          updateStage('windup');
          playSound(GUARDIAN_ATTACK_SOUNDS.charge, {
            preventOverlap: true,
          });
        }
      }, counterWindupAt),
      setTimeout(() => {
        if (result.retaliation) {
          updateStage('incoming');
          playSound(
            result.miniBoss
              ? GUARDIAN_ATTACK_SOUNDS.wardenReleases[
                  ((result.round || result.attacks || 1) - 1) %
                    GUARDIAN_ATTACK_SOUNDS.wardenReleases.length
                ]
              : GUARDIAN_ATTACK_SOUNDS.minionRelease,
            { preventOverlap: true }
          );
        }
      }, counterLaunchAt),
      setTimeout(() => {
        if (
          result.retaliation &&
          result.miniBoss &&
          result.damage > (result.boardDamage || 0)
        ) {
          playSound(GUARDIAN_ATTACK_SOUNDS.wardenContact, {
            preventOverlap: true,
          });
        }
      }, Math.max(0, counterAt - 1000)),
      setTimeout(() => {
        if (result.retaliation) {
          updateStage('counter');
          if (result.damage > (result.boardDamage || 0))
            combatHaptic('received');
          const shieldBlocked = result.boardShieldBlocked > 0;
          const impactSound =
            !result.miniBoss && !shieldBlocked
              ? '/sounds/punch-impact-hit.mp3'
              : '/sounds/dice-hits-board.mp3';
          if (shieldBlocked)
            playSound('/sounds/shield-used.mp3', { preventOverlap: true });
          if (
            (!shieldBlocked || result.damage > 0) &&
            (!result.miniBoss || result.damage === 0)
          )
            playSound(
              result.damage
                ? impactSound
                : '/sounds/short-energy-beam-shot.mp3',
              { preventOverlap: true }
            );
        }
      }, counterAt),
      setTimeout(() => {
        if (result.enemyHpAfter === 0) onVictoryPresented?.(game.lastResult);
        setGame(finishGuardianAttack);
      }, finishAt),
    ];
    return () => timers.forEach(clearTimeout);
  }, [
    combatId,
    startTime,
    launchAt,
    hitAt,
    counterWindupAt,
    counterLaunchAt,
    counterAt,
    finishAt,
    setGame,
    result?.enemyHpAfter,
    result?.retaliation,
    result?.damage,
    result,
    game.lastResult,
    onVictoryPresented,
    reduceMotion,
  ]);
  const room = getGuardianRoom(game);
  const displayRoom = result ? { ...room, target: result.enemyHpBefore } : room;
  const proxy = getGuardianRollGame(game);
  const displayGame = result
    ? { ...proxy, relics: result.relicsBefore }
    : proxy;
  const update = (operation) =>
    setGame((current) => updateGuardianThrow(current, operation));
  if (game.lastStandProtocol?.pending) return null;
  return (
    <div
      className={`coreboundGuardianBattle ${striking ? 'is-collecting' : ''}`}
    >
      <RollReveal
        key={game.floor + ':' + guardian.round}
        game={displayGame}
        room={displayRoom}
        effectiveRoll={
          result
            ? result.effectiveRoll
            : game.roll === null
            ? null
            : getGuardianAttack(game)
        }
        setup={setup}
        arrival={setup}
        lastAttack={setup ? guardian.lastRound : null}
        rollCharge={game.slingshotLaunch?.power ?? 0.72}
        rollLaunch={game.slingshotLaunch}
        restoreCompletedRoll={restoredRound === guardian.round}
        encounter={{
          enemy: guardian.enemy,
          maxHp: guardian.maxHp,
          hp: result
            ? stage === 'launch'
              ? result.enemyHpBefore
              : result.enemyHpAfter
            : guardian.hp,
          boss: guardian.isWarden,
          automatic: true,
          cameraHandoffRef,
          rewardOriginRef,
          onCombatReady,
          attacks: guardian.round,
          combat: result ? { ...result, ...timing, startTime, stage } : null,
          summary:
            'Attack ' +
            guardian.round +
            ' · Retaliation ' +
            room.damage +
            ' HP' +
            (guardian.isWarden
              ? ' · Loot: random relic + ' +
                getWardenShardReward(guardian.round) +
                ' shards'
              : guardian.round > 1
              ? ' · Base loot on victory'
              : game.floor === 4 || game.floor === 9
              ? ' · First strike: +1 shard'
              : ' · First strike: +1 shard + free hard portal'),
        }}
        onSelectDie={(id) => update((current) => selectDie(current, id))}
        onStartRoll={(launch) =>
          setGame((current) => beginGuardianRoll(current, launch))
        }
        onRollComplete={(roll, index, id, faceValues) =>
          setGame((current) =>
            settleGuardianRoll(current, roll, index, id, faceValues)
          )
        }
        onFlip={() => update(flipRoll)}
        onBoardTileChoice={(choice) =>
          update((current) => resolveBoardTileChoice(current, choice))
        }
        onPhaseAnchor={() => update(activatePhaseAnchor)}
        onPortalTransit={() => update(activatePortalPair)}
        onBoardBump={() =>
          update((current) => recordThrowEvent(current, 'bump'))
        }
        onMarkBounty={(index) =>
          update((current) => markBountyTile(current, index))
        }
        onClearBounty={() => update(clearBountyTag)}
        onResolve={() => setGame(startGuardianAttack)}
      />
      {canCollect && startTime > 0 && (
        <RewardCollection
          result={result}
          originRef={rewardOriginRef}
          healthRef={healthRef}
          shardsRef={shardsRef}
          startAt={startTime + rewardAt}
          reduceMotion={reduceMotion}
          onProgress={onRewardProgress}
        />
      )}
      {guardian.isWarden && !striking && (
        <p className="coreboundGuardianLootTiers">
          Guaranteed random relic. Shards: 1–3 attacks → 8 · 4–6 → 6 · 7+ → 4.
        </p>
      )}
    </div>
  );
};

export default GuardianBattle;
